import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useRubberBand } from '../../../hooks/useRubberBand';
import { Thread, ThreadMessage, Submission, Task, Nudge, TeamMember } from '../../../types';
import { ConversationThread } from '../../thread/ConversationThread';
import { markThreadReadForRole, batchMarkThreadMessagesRead } from '../../../services/firestoreService';
import './ParticipantInbox.css';
import './ParticipantInbox.glass.css';

export interface ParticipantInboxProps {
  isOpen: boolean;
  onClose: () => void;
  threads: Thread[];
  submissions: Submission[];
  tasks: Task[];
  threadsBySubmission?: Map<string, ThreadMessage[]>;
  threadContext?: {
    currentUserUid: string;
    currentUserName: string;
    organizationId: string;
  };
  /** Unread nudges for this participant, most-recent first. */
  nudges?: Nudge[];
  /** Read nudges within a recent window (e.g. 7 days), most-recent first.
      Rendered below the unread section so participants can re-read or
      re-navigate to a task after acknowledging the original. */
  recentReadNudges?: Nudge[];
  /** Used to resolve nudge.sentBy → sender name. Caller passes
      appData.teamMembers; the inbox uses the same composite-id-tolerant
      lookup as ParticipantNewPage. */
  teamMembers?: TeamMember[];
  /** Mark a nudge as read in Firestore. Wired by the parent. */
  onMarkNudgeAsRead?: (nudgeId: string) => void;
  /** Navigate to a specific task (by taskId) — used by the Go-to-task
      CTA on an expanded nudge row. The inbox will close itself first. */
  onNavigateToTask?: (taskId: string) => void;
  /** Navigate to a specific submission in the submissions drawer */
  onNavigateToSubmission?: (submissionId: string) => void;
}

function formatThreadTime(ts: any): string {
  if (!ts) return '';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  if (isNaN(date.getTime())) return '';
  const ms = Date.now() - date.getTime();
  if (ms < 60000) return 'now';
  if (ms < 3600000) return `${Math.floor(ms / 60000)}m`;
  if (ms < 86400000) return `${Math.floor(ms / 3600000)}h`;
  const days = Math.floor(ms / 86400000);
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function initial(name: string): string {
  return (name || '?')[0].toUpperCase();
}

/** Resolve a team-member sender name from a raw auth uid. Three id
    shapes to handle (matches the lookup used in ParticipantNewPage): */
function resolveSenderName(teamMembers: TeamMember[] | undefined, sentBy: string): string | null {
  if (!teamMembers) return null;
  const member = teamMembers.find(m =>
    m.userId === sentBy || m.id === sentBy || m.id.startsWith(`${sentBy}-`)
  );
  return member?.name || null;
}

export const ParticipantInbox: React.FC<ParticipantInboxProps> = ({
  isOpen,
  onClose,
  threads,
  submissions,
  tasks,
  threadsBySubmission,
  threadContext,
  nudges = [],
  recentReadNudges = [],
  teamMembers,
  onMarkNudgeAsRead,
  onNavigateToTask,
  onNavigateToSubmission,
}) => {
  const [closing, setClosing] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  /** Which nudge row is expanded in the inbox list. Only one at a time. */
  const [expandedNudgeId, setExpandedNudgeId] = useState<string | null>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  useRubberBand(listRef, isOpen && !activeThreadId);

  // Reset state when opening
  useEffect(() => {
    if (isOpen) {
      setClosing(false);
      setActiveThreadId(null);
      setExpandedNudgeId(null);
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      setActiveThreadId(null);
      onClose();
    }, 300);
  }, [onClose]);

  const handleBack = useCallback(() => {
    setActiveThreadId(null);
  }, []);

  if (!isOpen && !closing) return null;

  const activeThread = activeThreadId ? threads.find(t => t.id === activeThreadId) : null;
  const activeSubmission = activeThread
    ? submissions.find(s => s.id === activeThread.submissionId)
    : null;
  const activeTask = activeSubmission
    ? tasks.find(t => t.id === activeSubmission.taskId)
    : null;

  const taskMap = new Map<string, Task>(tasks.map(t => [t.id, t]));
  /** Visible rows (used for the empty-state check). */
  const totalCount = threads.length + nudges.length + recentReadNudges.length;
  /** Header badge counts only items that need attention: unread nudges
      plus threads that have any unread message. Recently-read nudges
      don't count. Hidden when 0. */
  const headerUnreadCount = nudges.length
    + threads.filter(t => (t.participantUnread || 0) > 0).length;

  /** Mark a nudge read and collapse the row. */
  const handleMarkNudgeRead = (nudgeId: string) => {
    onMarkNudgeAsRead?.(nudgeId);
    setExpandedNudgeId(null);
  };

  /** Mark the nudge read, close the inbox, and navigate to the task. */
  const handleGoToTaskForNudge = (nudgeId: string, taskId: string) => {
    onMarkNudgeAsRead?.(nudgeId);
    handleClose();
    setTimeout(() => onNavigateToTask?.(taskId), 320);
  };

  return (
    <div className={`pi-overlay ${closing ? 'pi-closing' : 'pi-open'}`}>
      <div className="pi-page" ref={pageRef}>
        {/* ── Header ── */}
        <div className="pi-header">
          <button className="pi-back" onClick={activeThreadId ? handleBack : handleClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 6l-6 6 6 6" />
            </svg>
          </button>
          <h1 className="pi-title">
            {activeThreadId ? (activeThread?.taskName || 'Conversation') : 'Messages'}
          </h1>
          {!activeThreadId && headerUnreadCount > 0 && (
            <span className="pi-count">{headerUnreadCount}</span>
          )}
        </div>

        {/* ── Inbox list — nudges + threads ── */}
        {!activeThreadId && (
          <div className="pi-list" ref={listRef}>
            {totalCount === 0 ? (
              <div className="pi-empty">
                <div className="pi-empty-icon">
                  <svg width="40" height="40" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 11.5l11 7 11-7" />
                    <rect x="5" y="9" width="22" height="14" rx="2" />
                  </svg>
                </div>
                <p className="pi-empty-text">Nothing here yet</p>
                <p className="pi-empty-sub">
                  When a researcher sends you a nudge or leaves feedback on your submissions, it will appear here.
                </p>
              </div>
            ) : (
              <>
                {/* ── Nudges section (only when there are unread nudges) ── */}
                {nudges.length > 0 && (
                  <>
                    <div className="pi-section-header">From your researcher</div>
                    {nudges.map(nudge => {
                      const expanded = expandedNudgeId === nudge.id;
                      const senderName = resolveSenderName(teamMembers, nudge.sentBy);
                      const senderFirst = senderName ? senderName.split(' ')[0] : 'Researcher';
                      const senderInitial = (senderName || 'R')[0].toUpperCase();
                      const task = nudge.taskId ? taskMap.get(nudge.taskId) : null;
                      const taskName = task?.title || task?.prompt;
                      return (
                        <div key={nudge.id} className={`pi-nudge ${expanded ? 'pi-nudge--expanded' : ''}`}>
                          <button
                            className="pi-nudge-row"
                            onClick={() => setExpandedNudgeId(expanded ? null : nudge.id)}
                            aria-expanded={expanded}
                          >
                            <div className="pi-thread-avatar">
                              <span>{senderInitial}</span>
                            </div>
                            <div className="pi-thread-body">
                              <div className="pi-thread-top">
                                <span className="pi-thread-name">{senderFirst}</span>
                                <span className="pi-row-dot" aria-hidden="true">·</span>
                                <span className="pi-thread-time">{formatThreadTime(nudge.sentAt)}</span>
                                <span className="pi-live-dot" aria-hidden="true" />
                              </div>
                              {!expanded && (
                                <div className="pi-thread-bottom">
                                  <span className="pi-thread-preview pi-nudge-preview">{nudge.message}</span>
                                </div>
                              )}
                              {!expanded && taskName && (
                                <span className="pi-nudge-task-chip">
                                  {taskName}
                                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M5 12h14M13 6l6 6-6 6" />
                                  </svg>
                                </span>
                              )}
                            </div>
                          </button>
                          {expanded && (
                            <div className="pi-nudge-body">
                              <p className="pi-nudge-message">{nudge.message}</p>
                              <div className="pi-nudge-actions">
                                {nudge.taskId && taskName && (
                                  <button
                                    className="pi-btn-primary"
                                    onClick={(e) => { e.stopPropagation(); handleGoToTaskForNudge(nudge.id, nudge.taskId!); }}
                                    type="button"
                                  >
                                    Go to task
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 6 }}>
                                      <polyline points="9,18 15,12 9,6" />
                                    </svg>
                                  </button>
                                )}
                                <button
                                  className="pi-btn-ghost"
                                  onClick={(e) => { e.stopPropagation(); handleMarkNudgeRead(nudge.id); }}
                                  type="button"
                                >
                                  Mark as read
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}

                {/* ── Recently-read nudges section — last 7 days, dimmed
                    styling, no "Mark as read" CTA (already read). Tap to
                    expand re-reads the body; "Go to task" still works. ── */}
                {recentReadNudges.length > 0 && (
                  <>
                    <div className="pi-section-header">Recently read</div>
                    {recentReadNudges.map(nudge => {
                      const expanded = expandedNudgeId === nudge.id;
                      const senderName = resolveSenderName(teamMembers, nudge.sentBy);
                      const senderFirst = senderName ? senderName.split(' ')[0] : 'Researcher';
                      const senderInitial = (senderName || 'R')[0].toUpperCase();
                      const task = nudge.taskId ? taskMap.get(nudge.taskId) : null;
                      const taskName = task?.title || task?.prompt;
                      return (
                        <div key={nudge.id} className={`pi-nudge pi-nudge--read ${expanded ? 'pi-nudge--expanded' : ''}`}>
                          <button
                            className="pi-nudge-row"
                            onClick={() => setExpandedNudgeId(expanded ? null : nudge.id)}
                            aria-expanded={expanded}
                          >
                            <div className="pi-thread-avatar">
                              <span>{senderInitial}</span>
                            </div>
                            <div className="pi-thread-body">
                              <div className="pi-thread-top">
                                <span className="pi-thread-name">{senderFirst}</span>
                                <span className="pi-row-dot" aria-hidden="true">·</span>
                                <span className="pi-thread-time">{formatThreadTime(nudge.sentAt)}</span>
                              </div>
                              {!expanded && (
                                <div className="pi-thread-bottom">
                                  <span className="pi-thread-preview pi-nudge-preview">{nudge.message}</span>
                                </div>
                              )}
                              {!expanded && taskName && (
                                <span className="pi-nudge-task-chip">
                                  {taskName}
                                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M5 12h14M13 6l6 6-6 6" />
                                  </svg>
                                </span>
                              )}
                            </div>
                          </button>
                          {expanded && (
                            <div className="pi-nudge-body">
                              <p className="pi-nudge-message">{nudge.message}</p>
                              {nudge.taskId && taskName && (
                                <div className="pi-nudge-actions">
                                  <button
                                    className="pi-btn-primary"
                                    onClick={(e) => { e.stopPropagation(); handleClose(); setTimeout(() => onNavigateToTask?.(nudge.taskId!), 320); }}
                                    type="button"
                                  >
                                    Go to task
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 6 }}>
                                      <polyline points="9,18 15,12 9,6" />
                                    </svg>
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}

                {/* ── Threads section ── */}
                {threads.length > 0 && (
                  <>
                    {(nudges.length > 0 || recentReadNudges.length > 0) && <div className="pi-section-header">On your submissions</div>}
                    {threads.map(thread => {
                const unread = thread.participantUnread || 0;
                const sub = submissions.find(s => s.id === thread.submissionId);
                const task = sub ? taskMap.get(sub.taskId || '') : null;
                const displayName = thread.taskName || task?.title || task?.prompt || 'Task';

                return (
                  <button
                    key={thread.id}
                    className={`pi-thread ${unread > 0 ? 'pi-thread--unread' : ''}`}
                    onClick={() => {
                      // Mark as read
                      if (unread > 0) {
                        markThreadReadForRole(thread.submissionId, 'participant').catch(() => {});
                        const msgs = threadsBySubmission?.get(thread.submissionId);
                        if (msgs) {
                          const unreadIds = msgs.filter(m => m.senderRole === 'researcher' && !m.readByRecipient).map(m => m.id);
                          if (unreadIds.length > 0) batchMarkThreadMessagesRead(unreadIds).catch(() => {});
                        }
                      }
                      // Navigate to submission
                      if (onNavigateToSubmission && thread.submissionId) {
                        handleClose();
                        setTimeout(() => onNavigateToSubmission(thread.submissionId), 320);
                      } else {
                        setActiveThreadId(thread.id);
                      }
                    }}
                  >
                    <div className="pi-thread-avatar">
                      <span>{initial(thread.lastMessageSenderName)}</span>
                    </div>
                    <div className="pi-thread-body">
                      <div className="pi-thread-top">
                        <span className="pi-thread-name">{displayName}</span>
                        <span className="pi-row-dot" aria-hidden="true">·</span>
                        <span className="pi-thread-time">{formatThreadTime(thread.lastMessageAt)}</span>
                      </div>
                      <div className="pi-thread-bottom">
                        <span className="pi-thread-preview">
                          {thread.lastMessageSenderRole === 'participant' ? 'You: ' : ''}
                          {thread.lastMessageBody}
                        </span>
                        {unread > 0 && (
                          <span className="pi-thread-badge">{unread}</span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* ── Active conversation ── */}
        {activeThreadId && activeSubmission && threadContext && (
          <div className="pi-conversation">
            {/* Submission context */}
            <div className="pi-context">
              <span className="pi-context-label">
                Re: {activeThread?.taskName || activeTask?.title || activeTask?.prompt || 'Submission'}
              </span>
            </div>
            <ConversationThread
              submissionId={activeSubmission.id}
              organizationId={threadContext.organizationId}
              projectId={activeSubmission.projectId}
              participantId={activeSubmission.participantId}
              currentUserUid={threadContext.currentUserUid}
              currentUserName={threadContext.currentUserName}
              currentUserRole="participant"
              messages={threadsBySubmission?.get(activeSubmission.id)}
              taskName={activeThread?.taskName || activeTask?.title || activeTask?.prompt}
            />
          </div>
        )}
      </div>
    </div>
  );
};
